/**
 * SedeContext - Gestione sede corrente
 *
 * Permette di:
 * - Memorizzare la sede corrente per l'utente
 * - Admin possono commutare tra sedi
 * - Operatori vedono solo la loro sede assegnata
 * - Persistenza in localStorage
 *
 * v2 (Opzione B refactor): le sedi sono caricate dinamicamente da
 * `GET /api/magazzino/sedi` (tabella `sedi`). `SedeType` è ora `string` aperta
 * (sede aggiunte da DB → disponibili senza ricompilare). `SEDI` resta mutabile
 * con seed default per compatibilità con consumer pre-fetch.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from '../components/auth/AuthProvider';

// Tipo aperto: la lookup table sedi è in DB, non vincolato a literal union.
export type SedeType = string;

export interface SedeInfo {
  id: SedeType;
  nome: string;
  nomeBreve: string;
  colore: string;
  coloreBg: string;
}

// Map mutabile: pre-popolata con default; aggiornata al primo fetch da /api/magazzino/sedi.
// Mantiene compatibilità con consumer che importano `SEDI` direttamente.
export const SEDI: Record<string, SedeInfo> = {
  misterbianco: { id: 'misterbianco', nome: 'Misterbianco', nomeBreve: 'MB', colore: 'text-white', coloreBg: 'bg-blue-600' },
  palermo:      { id: 'palermo',      nome: 'Palermo',      nomeBreve: 'PA', colore: 'text-white', coloreBg: 'bg-orange-600' },
};

interface SedeContextValue {
  sedeCorrente: SedeType;
  sedeInfo: SedeInfo;
  sedeAssegnata: SedeType | null;
  puoCambiareSede: boolean;
  cambiareSede: (sede: SedeType) => void;
  sediDisponibili: SedeInfo[];
  /** Map completa delle sedi caricate da DB (utile a consumer dinamici) */
  sedeMap: Record<string, SedeInfo>;
  isSedeCorrente: (sede: SedeType | null | undefined) => boolean;
  isLoading: boolean;
}

const SedeContext = createContext<SedeContextValue | null>(null);

const STORAGE_KEY = 'coronatek_sede_corrente';
const DEFAULT_SEDE: SedeType = 'misterbianco';

export function SedeProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [sedeCorrente, setSedeCorrente] = useState<SedeType>(DEFAULT_SEDE);
  const [sedeAssegnata, setSedeAssegnata] = useState<SedeType | null>(null);
  const [sedeMap, setSedeMap] = useState<Record<string, SedeInfo>>({ ...SEDI });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch lista sedi (al mount, indipendente da auth)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/magazzino/sedi');
        const data = await res.json();
        if (cancelled || !data.success || !Array.isArray(data.sedi)) return;
        const m: Record<string, SedeInfo> = {};
        for (const s of data.sedi) {
          const info: SedeInfo = {
            id: s.codice,
            nome: s.nome_display,
            nomeBreve: s.nome_breve || s.codice.slice(0, 2).toUpperCase(),
            colore: 'text-white',
            coloreBg: s.colore_bg || 'bg-blue-600',
          };
          m[s.codice] = info;
          // Aggiorna anche SEDI mutabile (per compat consumer che leggono direttamente)
          SEDI[s.codice] = info;
        }
        setSedeMap(m);
      } catch (e) {
        console.warn('[SedeContext] Fetch sedi fallito, uso default:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Carica sede utente
  useEffect(() => {
    if (authLoading) return;

    const loadSede = async () => {
      setIsLoading(true);
      try {
        if (user?.id) {
          const response = await fetch('/api/users/me/sede', {
            headers: { 'x-user-id': user.id }
          });
          if (response.ok) {
            const data = await response.json();
            const assegnata = data.sede_assegnata as SedeType | null;
            setSedeAssegnata(assegnata);
            if (assegnata) {
              setSedeCorrente(assegnata);
            } else {
              const saved = localStorage.getItem(STORAGE_KEY);
              if (saved) setSedeCorrente(saved);
            }
          }
        }
      } catch (error) {
        // le fetch abortite da navigazione/smontaggio non sono errori (audit e2e 2026-07-15)
        if (!String((error as Error)?.message || error).includes('Failed to fetch')) {
          console.error('[SedeContext] Errore caricamento sede:', error);
        }
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) setSedeCorrente(saved);
      } finally {
        setIsLoading(false);
      }
    };

    loadSede();
  }, [user?.id, authLoading]);

  const puoCambiareSede = !sedeAssegnata && (user?.role === 'admin' || user?.role === 'superadmin');

  const cambiareSede = useCallback((sede: SedeType) => {
    if (!puoCambiareSede) {
      console.warn('[SedeContext] Utente non autorizzato a cambiare sede');
      return;
    }
    setSedeCorrente(sede);
    localStorage.setItem(STORAGE_KEY, sede);
    console.log('[SedeContext] Sede cambiata:', sede);
  }, [puoCambiareSede]);

  const sediDisponibili = sedeAssegnata
    ? [sedeMap[sedeAssegnata] || SEDI[sedeAssegnata] || SEDI[DEFAULT_SEDE]]
    : Object.values(sedeMap);

  const isSedeCorrente = useCallback((sede: SedeType | null | undefined) => {
    if (!sede) return true;
    return sede === sedeCorrente;
  }, [sedeCorrente]);

  const value: SedeContextValue = {
    sedeCorrente,
    sedeInfo: sedeMap[sedeCorrente] || SEDI[sedeCorrente] || SEDI[DEFAULT_SEDE],
    sedeAssegnata,
    puoCambiareSede,
    cambiareSede,
    sediDisponibili,
    sedeMap,
    isSedeCorrente,
    isLoading
  };

  return (
    <SedeContext.Provider value={value}>
      {children}
    </SedeContext.Provider>
  );
}

export function useSede() {
  const context = useContext(SedeContext);
  if (!context) {
    throw new Error('useSede deve essere usato dentro SedeProvider');
  }
  return context;
}

export function useSedeCorrente(): SedeType {
  const context = useContext(SedeContext);
  return context?.sedeCorrente || DEFAULT_SEDE;
}

export default SedeContext;
